'use client';

// UI-only: always display full card details on the visual card

export type VirtualCard = {
  nickname: string;
  maskedCardNumber: string;
  expiryDate: string;
  cvv: string;
  cardHolderName?: string;
  fullCardNumber?: string;
  cardBgImage: string;
};

export type VirtualCardDisplayProps = {
  card: VirtualCard;
  showDetails: boolean;
  isActive: boolean;
  cardColor: string;
  onToggleVisibility: () => void;
};

export const mockVirtualCard: VirtualCard = {
  nickname: 'Everyday Spend',
  maskedCardNumber: '•••• •••• •••• 4821',
  fullCardNumber: '4242 4242 4242 4821',
  expiryDate: '08/28',
  cvv: '742',
  cardHolderName: 'Jordan Baker',
  cardBgImage: '/assets/bgcard.png',
};

export const mockVirtualCardDisplayProps: VirtualCardDisplayProps = {
  card: mockVirtualCard,
  showDetails: true,
  isActive: true,
  cardColor: 'from-blue-600 to-blue-900',
  onToggleVisibility: () => {},
};

export function VirtualCardDisplay({
  card,
  showDetails,
  isActive,
  cardColor,
  onToggleVisibility,
}: VirtualCardDisplayProps) {
  const cardOpacity = isActive ? 'opacity-100' : 'opacity-50';

  return (
    <div
      className={`relative flex-shrink-0 rounded-[16px]  border-white/10 text-white p-6 py-14 flex flex-col justify-between overflow-hidden transform transition-transform duration-300 hover:scale-105 bg-gradient-to-br ${cardColor} shadow-xl ${cardOpacity} min-h-[210px]`}
    >
      {/* Background texture */}
      <div className="absolute inset-0 z-0 pointer-events-none select-none">
        <img
          src={card.cardBgImage}
          alt="Virtual Card Background texture"
          className="w-full h-full object-cover"
          style={{
            maskImage: 'linear-gradient(to bottom left, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom left, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 100%)',
          }}
        />
      </div>

      {/* Decorative Brand Circles */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full transform translate-x-12 -translate-y-12 opacity-50 pointer-events-none select-none" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full transform -translate-x-8 translate-y-8 opacity-50 pointer-events-none select-none" />

      {/* Glass/Glow reflection highlight overlay */}
      <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/[0.04] to-white/[0.1] z-0 pointer-events-none" />

      {/* Card Contents */}
      <div className="relative z-10 flex flex-col justify-between h-full flex-grow space-y-4">
        {/* Top Section: Info + Brand Logo */}
        <div className="flex justify-between items-start">
          <div>
            <span className="text-[9px] uppercase tracking-[0.2em] font-mono text-white/60">Topup Card</span>
            <p className="font-bold text-lg tracking-wide text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] mt-0.5">{card.nickname}</p>
          </div>
          {/* Brand Logo in Premium Pill */}
         
        </div>

        {/* Middle Section: Balance/Card Number + Chip */}
        <div className="flex justify-between items-center mt-2">
          <p className="text-2xl font-mono font-bold tracking-wider text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
            {card.fullCardNumber ?? card.maskedCardNumber}
          </p>
          <img src="/assets/chip.webp" alt="Chip" className="w-16 h-16 -mt-12 object-contain rounded-md rotate-90" />
        </div>

        {/* Bottom Section: Details + Payment Partner Logo */}
        <div className="flex justify-between items-end pt-5">
          <div className="flex gap-6 items-end">
            {/* Card Holder Name */}
            <div>
              <p className="text-[8px] uppercase tracking-widest text-white/50">Card Holder</p>
              <p className="text-xs font-semibold font-mono uppercase tracking-wide text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)]">
                {card.cardHolderName || 'User Profile'}
              </p>
            </div>
            
            {/* Expiry / Equivalent */}
            {card.expiryDate && (
              <div>
                <p className="text-[8px] uppercase tracking-widest text-white/50">
                  {card.expiryDate.includes('LKR') ? 'LKR EQUIV' : card.expiryDate.includes('USDT') ? 'USDT EQUIV' : 'VALID THRU'}
                </p>
                <p className="text-xs font-semibold font-mono text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)]">
                  {card.expiryDate}
                </p>
              </div>
            )}
          </div>

          {/* eZ Cash payment partner logo */}
          <div className="flex items-center gap-3 -mr-8 ">
            <img src="/assets/ez-cash-logo.png" alt="eZ Cash" className="h-8 object-contain drop-shadow-md" />
            <img src="/assets/c.png" alt="Brand Logo" className="h-8 object-contain bg-white w-20 rounded-md pr-2" />
          </div>
        </div>
      </div>
    </div>
  );
}